import { keepPreviousData, useQuery } from '@tanstack/vue-query';
import { computed, type ComputedRef, type Ref } from 'vue';
import http from '@/services/http';
import type { ApiResponse, TrackMatchingCursor } from '@/types/types';
import { filterNullValues } from '@/utils/filterUtils';
import type { IScope } from '@/types/filterBuilderTypes';
import type { SortDirection } from '@/types/tableTypes';

export interface SearchApiParams {
  /** Cursor request: an opaque token (or 'last' sentinel / null) plus whether to count the total. */
  cursor: Ref<TrackMatchingCursor>;
  pageSize: Ref<number> | ComputedRef<number>;
  sorting?: Ref<object> | ComputedRef<object>;
  filter?: Ref<object> | ComputedRef<object>;
  entitySlug: ComputedRef<string>;
}

/**
 * The `filter` node a search request carries. A type alias rather than an
 * interface so it stays assignable to the export/queue source's looser
 * `{ ruleScopes?: IScope; [key: string]: unknown }` filter.
 */
export type SearchFilterPayload = {
  ruleScopes?: IScope;
  geoRadius?: { pins: unknown[] };
};

/**
 * The filter the results on screen were actually fetched with.
 *
 * The persisted builder state is not sendable as-is: a rule the user has not
 * finished (no column, no criterion, no value) is dropped here, and a map
 * radius with no pins is dropped too. Returns undefined when nothing is left.
 *
 * Shared with the Search page's export and play-queue sources on purpose. Both
 * describe "the list the user is looking at", and that list was fetched
 * through this cleaning — a source that sent the raw builder state would
 * name a different list from the one on screen.
 */
export function buildSearchFilterPayload(rawFilter: unknown): SearchFilterPayload | undefined {
  if (!rawFilter || typeof rawFilter !== 'object') {
    return undefined;
  }

  let ruleScopesPayload: IScope | undefined;
  if ('ruleScopes' in rawFilter && rawFilter.ruleScopes) {
    const cleanedScope = filterNullValues(rawFilter.ruleScopes as IScope);
    if (cleanedScope) {
      ruleScopesPayload = cleanedScope;
    }
  }

  const geo = (rawFilter as { geoRadius?: { pins?: unknown[] } }).geoRadius;
  const geoRadiusPayload =
    geo && Array.isArray(geo.pins) && geo.pins.length > 0
      ? (geo as { pins: unknown[] })
      : undefined;

  if (!ruleScopesPayload && !geoRadiusPayload) {
    return undefined;
  }

  const cleanedFilter: SearchFilterPayload = {};
  if (ruleScopesPayload) {
    cleanedFilter.ruleScopes = ruleScopesPayload;
  }
  if (geoRadiusPayload) {
    cleanedFilter.geoRadius = geoRadiusPayload;
  }
  return cleanedFilter;
}

/**
 * A search response, with the sort its request sent. The custom list table orders each row's
 * schedule lines by it. It is not the live sort: while a new sort loads, keepPreviousData keeps
 * the old rows on screen, and they keep the line order of the sort that placed them.
 */
export type SearchApiResponse = ApiResponse & {
  /** The sort the request sent, keyed by field in priority order. Undefined when it sent none. */
  sentSort?: Record<string, SortDirection>;
};

export function useSearchApi({ entitySlug, cursor, pageSize, sorting, filter}: SearchApiParams) {
  const queryKey = ['search', entitySlug, cursor, pageSize, sorting, filter];
  const queryFn = () => {
    // A copy, because the live sort is changed in place when a header is clicked.
    const sentSort = sorting?.value ? { ...(sorting.value as Record<string, SortDirection>) } : undefined;
    return http
      .post(`/api/${entitySlug?.value}/search`, {
          perPage: pageSize?.value,
          cursor: cursor.value.token ?? undefined,
          last: cursor.value.last || undefined,
          withTotal: cursor.value.withTotal,
          sort: sorting?.value || undefined,
          filter: buildSearchFilterPayload(filter?.value),
      })
      .then((response): SearchApiResponse => ({ ...(response.data as ApiResponse), sentSort }));
};
  // keepPreviousData keeps the previous page's rows visible during a cursor hop instead of
  // clearing to undefined (which flashed the table/cards/map empty) — matching TrackMatchingApi.
  const query = useQuery({queryFn, queryKey, retry: 0, enabled: computed(() => Boolean(entitySlug.value)), placeholderData: keepPreviousData});
  
  // Use isFetching instead of isLoading to show loading state during refetches (e.g., when sorting)
  // isLoading only returns true on initial load when there's no data
  // isFetching returns true whenever a fetch is in progress, including refetches
  return {
    ...query,
    isLoading: computed(() => query.isFetching.value || query.isLoading.value),
  };
}

